import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ingredientsData, getCategoryInfo } from '../data/ingredientsData';

function IngredientDetail() {
  const { ingredientId } = useParams();
  const ingredient = ingredientsData[ingredientId];
  const categoryInfo = getCategoryInfo();

  if (!ingredient) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-charcoal mb-4">Ingredient Not Found</h1>
          <p className="text-gray-500 mb-8">The ingredient you're looking for doesn't exist in our library.</p>
          <Link to="/ingredients" className="text-sage-600 hover:text-sage-700 font-medium">
            ← Back to Ingredients Library
          </Link>
        </div>
      </div>
    );
  }

  const categoryIcon = categoryInfo[ingredient.categorySlug]?.icon || '📋';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{ingredient.name} - Research & Health Effects | FoodLens AI</title>
        <meta name="description" content={`${ingredient.description} Learn about health concerns, scientific research, and expert analysis of ${ingredient.name.toLowerCase()}.`} />
        <meta name="keywords" content={`${ingredient.name.toLowerCase()}, ${ingredient.category.toLowerCase()}, food ingredients, health effects, nutrition research, scientific studies`} />
        <meta name="author" content="FoodLens AI Research Team" />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${ingredient.name} - Research & Health Effects`} />
        <meta property="og:description" content={ingredient.description} />
        <meta property="og:url" content={`https://foodlens-ai.com/ingredients/${ingredient.id}`} />
        <meta property="og:site_name" content="FoodLens AI" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${ingredient.name} - Research & Health Effects`} />
        <meta name="twitter:description" content={ingredient.description} />

        {/* Article specific */}
        <meta property="article:section" content={ingredient.category} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://foodlens-ai.com/ingredients/${ingredient.id}`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb Navigation */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center space-x-2 text-gray-500">
            <li>
              <Link to="/" className="hover:text-sage-600 transition-colors">Home</Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <Link to="/ingredients" className="hover:text-sage-600 transition-colors">Ingredients Library</Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-charcoal font-medium">{ingredient.name}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center mb-4">
            <span className="text-4xl mr-4">{categoryIcon}</span>
            <div>
              <div className="mb-2">
                <span className="inline-block bg-sage-100 text-sage-700 px-3 py-1 rounded-lg text-sm font-medium">
                  {ingredient.category}
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl text-charcoal">
                {ingredient.name}
              </h1>
            </div>
          </div>
        </header>

        {/* Overview Section */}
        <section className="mb-12 p-6 bg-sage-50 rounded-2xl border border-sage-100">
          <h2 className="font-display text-2xl text-charcoal mb-4">Overview</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {ingredient.description}
          </p>
        </section>

        {/* Health Concerns Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Health Concerns</h2>
          <div className="space-y-4">
            {ingredient.concerns.map((concern, index) => (
              <div key={index} className="flex items-start p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl">
                <svg className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-gray-700">{concern}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Scientific Research Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Scientific Research</h2>
          <p className="text-gray-500 mb-6">
            The following peer-reviewed studies and research papers provide scientific evidence about {ingredient.name.toLowerCase()}:
          </p>
          <div className="space-y-6">
            {ingredient.references.map((ref, index) => (
              <article key={index} className="p-6 bg-surface border border-sage-100 rounded-2xl hover:shadow-soft transition-shadow">
                <h3 className="font-semibold text-charcoal mb-3">
                  {ref.title}
                </h3>

                <div className="mb-3 text-sm text-gray-500">
                  <p className="mb-1">
                    <span className="font-medium">Authors:</span> {ref.authors}
                  </p>
                  <p className="mb-1">
                    <span className="font-medium">Published in:</span> {ref.publication} ({ref.year})
                  </p>
                  {ref.doi && (
                    <p className="mb-1">
                      <span className="font-medium">DOI:</span> {ref.doi}
                    </p>
                  )}
                </div>

                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sage-600 hover:text-sage-700 font-medium transition-colors"
                >
                  View Full Study
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                </a>
              </article>
            ))}
          </div>
        </section>

        {/* How FoodLens Helps Section */}
        <section className="mb-12 p-8 bg-gradient-sage rounded-2xl text-white">
          <h2 className="font-display text-2xl mb-4">
            Detect {ingredient.name} with FoodLens AI
          </h2>
          <p className="text-lg text-white/90 mb-6">
            FoodLens AI instantly identifies {ingredient.name.toLowerCase()} in food labels, along with hundreds of other
            potentially harmful ingredients. Simply scan any ingredient label to get an instant analysis.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/app"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
            >
              Download FoodLens AI
            </Link>
            <Link
              to="/ingredients"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-medium rounded-xl border border-white/30 hover:bg-white/20 transition-colors"
            >
              Browse More Ingredients
            </Link>
          </div>
        </section>

        {/* Related Articles */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Related Articles</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Link
              to="/blog/understanding-seed-oils"
              className="p-6 bg-surface border border-sage-100 rounded-2xl hover:shadow-soft hover:-translate-y-1 transition-all"
            >
              <h3 className="font-semibold text-charcoal mb-2">Understanding Seed Oils</h3>
              <p className="text-sm text-gray-500">Learn why seed oils are hidden in your food and their health implications.</p>
            </Link>
            <Link
              to="/blog/reading-ingredient-labels"
              className="p-6 bg-surface border border-sage-100 rounded-2xl hover:shadow-soft hover:-translate-y-1 transition-all"
            >
              <h3 className="font-semibold text-charcoal mb-2">Reading Ingredient Labels</h3>
              <p className="text-sm text-gray-500">Master the art of decoding food labels and identifying harmful ingredients.</p>
            </Link>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="p-6 bg-sage-50 border-l-4 border-sage-300 rounded-r-xl text-sm text-gray-600">
          <p className="font-semibold mb-2 text-charcoal">Disclaimer</p>
          <p>
            This information is provided for educational purposes only and is not intended as medical advice.
            The research citations and health concerns listed are based on available scientific literature.
            Always consult with healthcare professionals for personalized dietary advice.
          </p>
        </div>
      </div>
    </div>
  );
}

export default IngredientDetail;
